/* 
 *   ____                                           _   
 *  |  _ \ _ __ ___  __ _ _ __ ___   __ _  ___  ___| |_ 
 *  | | | | '__/ _ \/ _` | '_ ` _ \ / _` |/ _ \/ __| __|
 *  | |_| | | |  __/ (_| | | | | | | (_| |  __/\__ \ |_ 
 *  |____/|_|  \___|\__,_|_| |_| |_|\__, |\___||___/\__|
 *                                  |___/     
 *           
 * Copyright 2021 Dreamgest ltd <help@dreamgest.com>.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* global Promise, e */

'use strict';

const API_HOST = 'https://api.dreamgest.com/drpass/';
const APP_ID = 'e04f742273c01cee9086aa1252b08123';

const server = {
  host: API_HOST,
  authToken: async (token) => {
    if (token) {
      await myStorage.set('authToken', token);
      return token;
    } else {
      let t = await myStorage.get('authToken');
      return t || '';
    }
  },
  applicationId: APP_ID,
  /**
   * Invia una richiesta asincrona al server tramite fetch() e ritorna la risposta con i suoi metodi.
   * Per maggiori info vedere:
   * https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
   * 
   *  Si possono utilizzare i seguenti shortcuts:
   *  - .get(api)           -> .request(api, 'GET')
   *  - .post(api, data)    -> .request(api, 'POST', data) 
   *  - .put(api, data)     -> .request(api, 'PUT', data) 
   *  - .patch(api, data)   -> .request(api, 'PATCH', data) 
   *  - .delete(api)        -> .request(api, 'DELETE')
   *   
   * @param {type} api
   * @param {type} method
   * @param {type} data
   * @param {type} headers
   * @returns {server.server=>#45.resp}
   */
  request: async (api, method, data, headers) => {
    const token = await server.authToken();
    const options = {
      method: method || 'GET',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        'Auth-Token': token,
        'Application-Id': server.applicationId,
      },
    }

    let language = await myStorage.get('language');

    if (language) {
      options.headers['Accept-Language'] = language;
    }

    if (headers) {
      headers.forEach((k, v) => options.headers[k] = v);
    }

    if (['POST', 'PUT', 'PATCH'].indexOf(method) !== -1) {
      options.body = data ? JSON.stringify(data) : null;
    }
    const url = server.host + api;
    try {
      let resp = await fetch(url, options);
      return resp;
    } catch (e) {
      console.log(e);
      return Promise.reject({message: lng.error_connection})
    }
  },
  get: async (api) => server.request(api),
  delete: async (api) => server.request(api, 'DELETE'),
  post: async (api, data) => server.request(api, 'POST', data),
  put: async (api, data) => server.request(api, 'PUT', data),
  patch: async (api, data) => server.request(api, 'PATCH', data),
  getQrcode: async () => {
    try {
      let data = await server.getJSON('user/qrcode/' + (myApp.children ? '?children=' + myApp.children : '')),
        qrcodekey = myApp.children ? 'qrcode_' + myApp.children : 'qrcode';
      await myStorage.set(qrcodekey, data.qrcode);
      return data.qrcode;
    } catch (err) {
      console.log('getQrcode error. ' + err.message);
      return Promise.reject(err.message);
    }
  },
  /**
   * Invia una richiesta asincrona al server tramite fetch() e ritorna i dati generati tramite resp.json()
   * Se la risposta non è ok (status 200 ~ 299) genererà una Promise.reject()
   * 
   * Per maggiori info vedere:
   * https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
   * 
   *  Si possono utilizzare i seguenti shortcuts:
   *  - .getJSON(api)           -> .requestJSON(api, 'GET')
   *  - .postJSON(api, data)    -> .requestJSON(api, 'POST', data) 
   *  - .putJSON(api, data)     -> .requestJSON(api, 'PUT', data) 
   *  - .patchJSON(api, data)   -> .requestJSON(api, 'PATCH', data) 
   *  - .deleteJSON(api)        -> .requestJSON(api, 'DELETE')
   *   
   * @param {type} api
   * @param {type} method
   * @param {type} data
   * @param {type} headers
   * @returns {server.server=>#45.resp}
   */
  requestJSON: async (api, method, data, headers) => {
    const token = await server.authToken();
    const options = {
      method: method || 'GET',
      headers: {
        'Content-Type': 'application/json;charset=utf-8',
        'Auth-Token': token,
        'Application-Id': server.applicationId,
      },
    }

    let language = await myStorage.get('language');

    if (language) {
      options.headers['Accept-Language'] = language;
    }

    if (headers) {
      headers.forEach((k, v) => options.headers[k] = v);
    }

    if (['POST', 'PUT', 'PATCH'].indexOf(method) !== -1) {
      options.body = data ? JSON.stringify(data) : null;
    }
    const url = server.host + api;

    try {
      let res = await fetch(url, options);
      let data = await res.json();
      if (res.ok) return data;
      else return Promise.reject(data.error);
    } catch (e) {
      console.log(e, 'requestJSON fetch error');
      return Promise.reject({message: lng.error_connection});
    }
  },
  getJSON: async (api) => server.requestJSON(api),
  deleteJSON: async (api) => server.requestJSON(api, 'DELETE'),
  postJSON: async (api, data) => server.requestJSON(api, 'POST', data),
  putJSON: async (api, data) => server.requestJSON(api, 'PUT', data),
  patchJSON: async (api, data) => server.requestJSON(api, 'PATCH', data),
  readFile: async (filepath) => new Promise((resolve, reject) => {
      const gotFile = (fileEntry) => {
        fileEntry.file(function (file) {
          var reader = new FileReader();
          reader.onloadend = function (e) {
            resolve(this.result);
          }
          reader.readAsText(file);
        });
      }
      const fail = (err) => reject(err);

      window.resolveLocalFileSystemURL(cordova.file.applicationDirectory + "www/" + filepath, gotFile, fail);
    }),
}

export default server;